/**
 * cart-free-gifts.js
 *
 * Keeps free gift and "tree planted" line items in sync with the number of
 * paid boxes in the cart.  Configuration comes from window.__cartFreeGiftsConfig
 * which is injected by theme.liquid from Shopify theme settings.
 *
 * Algorithm:
 *  1. Count paid box qty (exclude managed variant IDs).
 *  2. For each gift variant, find the highest threshold tier that is met and
 *     use its quantity.  If no tier is met → quantity 0 (remove).
 *  3. For the trees variant → quantity = paid box count.
 *  4. Build an updates map (variant_id → qty) and an adds array, then POST to
 *     cart/update.js and/or cart/add.js in one round-trip each.
 *
 * Javvy: flavor lines carry `_javvy_cfg` (qty → selling-plan id + gift tiers).
 * After a cart qty edit (drawer or /cart, including back-from-checkout), those
 * lines are remapped to the matching plan so per-box price follows the tier,
 * then gifts are reconciled against that same map instead of theme defaults.
 *
 * Shared gift SKUs from different paid products are separate line items:
 * each add is tagged `_free_gift_for` with that product's id, and qty is
 * reconciled per owner (never via variant-id totals, which would merge them).
 *
 * Exposes:  window.cartFreeGifts.sync() → Promise<{changed: boolean, sections?: object}>
 *   changed is true if the cart was mutated. When true, sections is the fresh
 *   cart-drawer/cart-icon-bubble HTML already returned by the mutating
 *   request (update.js/add.js both accept a `sections` param), so callers
 *   can apply it directly instead of paying for a separate GET round trip.
 */
(function () {
  'use strict';

  if (window.__cartFreeGiftsInstalled) return;
  window.__cartFreeGiftsInstalled = true;

  var syncing = false;
  // If sync() is called again while one is already running, don't just drop
  // it — a caller relying on that request to reconcile e.g. a just-emptied
  // cart would otherwise leave stale gifts behind with nothing to correct
  // them. Queue exactly one follow-up run (with a fresh /cart.js fetch, not
  // whatever stale cartData triggered the dropped call) once the current
  // sync finishes. Callers of the dropped sync() wait on syncWaiters until
  // that follow-up (or the in-flight run) actually finishes — otherwise
  // checkout redirects would fire with gifts still in flight.
  var syncPending = false;
  var syncWaiters = [];

  function normalizeSyncResult(result) {
    if (result && typeof result === 'object') return result;
    return { changed: !!result };
  }

  function notifySyncWaiters(result) {
    var waiters = syncWaiters;
    syncWaiters = [];
    var payload = normalizeSyncResult(result);
    waiters.forEach(function (resolve) { resolve(payload); });
  }

  // If a dropped sync() overlapped a run that already mutated the cart
  // (e.g. gift-only clear), waiters must not resolve `{ changed: false }`
  // or callers will paint the pre-clear snapshot and re-enable checkout.
  var syncMutated = false;
  var drainingWaiters = false;

  function finishSync(result) {
    var payload = normalizeSyncResult(result);
    if (payload.changed) syncMutated = true;
    syncing = false;

    if (syncPending) {
      syncPending = false;
      drainingWaiters = true;
      window.__cartFreeGiftsSyncing = true;
      sync().then(function (followUp) {
        drainingWaiters = false;
        window.__cartFreeGiftsSyncing = false;
        var next = normalizeSyncResult(followUp);
        notifySyncWaiters({
          changed: syncMutated || next.changed,
          sections: next.sections
        });
        syncMutated = false;
      }, function () {
        drainingWaiters = false;
        window.__cartFreeGiftsSyncing = false;
        notifySyncWaiters({ changed: syncMutated });
        syncMutated = false;
      });
      return;
    }

    window.__cartFreeGiftsSyncing = false;
    // Follow-up run: the queued callers are notified by the parent finishSync
    // with a merged result so they don't apply a stale change.js snapshot.
    if (drainingWaiters) return;

    if (syncMutated) payload.changed = true;
    syncMutated = false;
    notifySyncWaiters(payload);
  }

  // Capture fetch now — before cart-drawer.js and Shrine's main.js (both deferred,
  // execute after this file) add their own patches. Using this reference means our
  // gift-sync mutations are invisible to Shrine, so Shrine cannot fire cart:open
  // in response to our internal cart/add.js calls and trigger the empty-cart overlay.
  //
  // Exposed globally because this is the earliest-loading script in the cart
  // stack — cart-drawer.js loads after this file but still before Shrine, so
  // it can capture its own; nw-new-bundle.js loads in the body, well after
  // Shrine's <head> script tag, so a same-file capture there would already be
  // Shrine-wrapped and too late. Both reuse this one instead of each needing
  // their own (im)possible-to-get-right capture.
  var _fetch = window.fetch.bind(window);
  if (!window.__originalFetch) window.__originalFetch = _fetch;

  // ── Helpers ────────────────────────────────────────────────────────────────

  function cartRoot() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.slice(-1) === '/' ? r : r + '/';
  }

  function fetchCart() {
    return _fetch(cartRoot() + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (res) {
      if (!res.ok) throw new Error('cart.js ' + res.status);
      return res.json();
    });
  }

  function sectionIds() {
    var ids = 'cart-drawer,cart-icon-bubble';
    if (document.getElementById('main-cart-items')) ids += ',cart-items,cart-footer';
    return ids;
  }

  function postUpdate(updates) {
    return _fetch(cartRoot() + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates, sections: sectionIds() }),
    }).then(function (res) {
      if (!res.ok) throw new Error('cart/update.js ' + res.status);
      return res.json();
    });
  }

  function postAdd(items) {
    return _fetch(cartRoot() + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items, sections: sectionIds() }),
    }).then(function (res) {
      if (!res.ok) throw new Error('cart/add.js ' + res.status);
      return res.json();
    });
  }

  function postChange(payload) {
    payload.sections = sectionIds();
    return _fetch(cartRoot() + 'cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (res) {
      if (!res.ok) throw new Error('cart/change.js ' + res.status);
      return res.json();
    });
  }

  function postDiscount(codes) {
    var list = (codes || []).map(function (c) { return String(c || '').trim(); }).filter(Boolean);
    if (!list.length) return Promise.resolve(null);
    return _fetch(cartRoot() + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: list.join(','), sections: sectionIds() }),
    }).then(function (res) {
      if (!res.ok) throw new Error('cart/update.js discount ' + res.status);
      return res.json();
    });
  }

  function discountCodesFromCart(cart) {
    var out = [];
    var seen = {};
    function push(raw) {
      var code = String(raw || '').trim();
      if (!code) return;
      var key = code.toUpperCase();
      if (seen[key]) return;
      seen[key] = true;
      out.push(code);
    }
    if (cart && Array.isArray(cart.discount_codes)) {
      cart.discount_codes.forEach(function (entry) {
        push(typeof entry === 'string' ? entry : entry && entry.code);
      });
    }
    if (cart && typeof cart.discount_code === 'string') push(cart.discount_code);
    return out;
  }

  function restoreDiscountCodes(savedCodes, latestCart, latestSections) {
    if (!savedCodes || !savedCodes.length) {
      return Promise.resolve({ sections: latestSections, cart: latestCart });
    }
    var p = (latestCart && Array.isArray(latestCart.items))
      ? Promise.resolve(latestCart)
      : fetchCart();
    return p.then(function (latest) {
      var current = discountCodesFromCart(latest);
      var missing = savedCodes.filter(function (c) {
        return !current.some(function (x) { return x.toUpperCase() === c.toUpperCase(); });
      });
      if (!missing.length) return { sections: latestSections, cart: latest };
      var merged = current.slice();
      savedCodes.forEach(function (c) {
        if (!merged.some(function (x) { return x.toUpperCase() === c.toUpperCase(); })) merged.push(c);
      });
      return postDiscount(merged).then(function (res) {
        return { sections: (res && res.sections) || latestSections, cart: res };
      });
    }).catch(function () {
      return { sections: latestSections, cart: latestCart };
    });
  }

  function postClear() {
    return _fetch(cartRoot() + 'cart/clear.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: '{}',
    }).then(function (res) {
      if (!res.ok) throw new Error('cart/clear.js ' + res.status);
      return res.json();
    });
  }

  // A cart made up entirely of $0 lines (tier gifts, tree-planted, or any
  // other free item) has nothing to check out — this happens when the
  // shopper manually removes the last paid box (cart page or drawer) and
  // leaves orphaned gift lines behind, since those are normally only
  // reconciled against paid quantity, never independently removed.
  function isGiftOnlyCart(cart) {
    return !!(cart && cart.items && cart.items.length &&
      cart.items.every(function (item) { return parseInt(item.final_price, 10) === 0; }));
  }

  // Lightweight, standalone check (own /cart.js fetch) for callers outside
  // sync()'s own flow — e.g. a fresh page load, where no mutation has fired
  // to trigger sync() itself.
  function clearIfGiftOnly() {
    return fetchCart().then(function (cart) {
      if (!isGiftOnlyCart(cart)) return false;
      return postClear().then(function () { return true; });
    }).catch(function (e) {
      console.error('[cart-free-gifts] gift-only clear error:', e);
      return false;
    });
  }

  // ── Gift ownership ────────────────────────────────────────────────────────
  // Gift lines are tagged with the product that unlocked them (line-item
  // property `_free_gift_for`) so that pages whose config doesn't manage that
  // variant (cart page, drawer on another PDP, home) can still drop them once
  // the owning product leaves the cart. A localStorage map covers lines added
  // before tagging existed.
  var OWNER_PROP = '_free_gift_for';
  var OWNER_STORE = 'larineFreeGiftOwners';

  function readOwnerMap() {
    try {
      return JSON.parse(localStorage.getItem(OWNER_STORE) || '{}') || {};
    } catch (e) {
      return {};
    }
  }

  function rememberOwners(variantIds, productId) {
    if (!productId) return;
    try {
      var map = readOwnerMap();
      variantIds.forEach(function (vid) { map[String(vid)] = String(productId); });
      localStorage.setItem(OWNER_STORE, JSON.stringify(map));
    } catch (e) {}
  }

  function giftOwner(item, ownerMap) {
    var fromProp = item.properties && item.properties[OWNER_PROP];
    if (fromProp) return String(fromProp);
    var fromMap = ownerMap[String(item.variant_id)];
    return fromMap ? String(fromMap) : null;
  }

  // ── Javvy cart tiers (selling plan + gifts follow box qty) ───────────────
  var JAVVY_CFG_PROP = '_javvy_cfg';
  var JAVVY_STORE = 'larineJavvyCartTiers';

  function parseJsonSafe(raw) {
    if (raw == null || raw === '') return null;
    if (typeof raw === 'object') return raw;
    try {
      return JSON.parse(String(raw));
    } catch (e) {
      return null;
    }
  }

  function normalizePlans(list) {
    if (!Array.isArray(list)) return [];
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var row = list[i];
      var minQty;
      var sellingPlanId;
      if (Array.isArray(row)) {
        minQty = Number(row[0]) || 0;
        sellingPlanId = Number(row[1]) || 0;
      } else if (row && typeof row === 'object') {
        minQty = Number(row.minQty) || 0;
        sellingPlanId = Number(row.sellingPlanId || row.id) || 0;
      } else {
        continue;
      }
      if (minQty > 0 && sellingPlanId > 0) out.push({ minQty: minQty, sellingPlanId: sellingPlanId });
    }
    return out;
  }

  function normalizeGifts(list) {
    if (!Array.isArray(list)) return [];
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var row = list[i];
      var variantId;
      var minQty;
      var quantity;
      if (Array.isArray(row)) {
        variantId = Number(row[0]) || 0;
        minQty = Number(row[1]) || 0;
        quantity = Number(row[2]) || 1;
      } else if (row && typeof row === 'object') {
        variantId = Number(row.variantId) || 0;
        minQty = Number(row.minQty) || 0;
        quantity = Number(row.quantity) || 1;
      } else {
        continue;
      }
      if (variantId > 0) out.push({ variantId: variantId, minQty: minQty, quantity: quantity });
    }
    return out;
  }

  function parseJavvyCfgObject(obj) {
    if (!obj || typeof obj !== 'object') return { plans: [], gifts: [] };
    return {
      plans: normalizePlans(obj.p || obj.plans),
      gifts: normalizeGifts(obj.g || obj.gifts)
    };
  }

  function readStoredJavvyCfg() {
    try {
      return parseJavvyCfgObject(parseJsonSafe(localStorage.getItem(JAVVY_STORE)));
    } catch (e) {
      return { plans: [], gifts: [] };
    }
  }

  function lineSellingPlanId(item) {
    var alloc = item && item.selling_plan_allocation;
    if (!alloc) return 0;
    if (alloc.selling_plan_id) return Number(alloc.selling_plan_id) || 0;
    if (alloc.selling_plan && alloc.selling_plan.id) return Number(alloc.selling_plan.id) || 0;
    return 0;
  }

  function lineJavvyCfg(item) {
    var props = item && item.properties;
    if (!props) return null;
    return parseJavvyCfgObject(parseJsonSafe(props[JAVVY_CFG_PROP]));
  }

  function pickPlanForQty(plans, qty) {
    if (!plans.length || qty <= 0) return null;
    // Same rule as gift tiers: highest threshold the qty actually meets.
    // 7 boxes stays on the 6-box plan ($10.99); $9.99 only from 8+.
    var met = plans.filter(function (p) { return p.minQty <= qty; })
      .sort(function (a, b) { return b.minQty - a.minQty; });
    if (met.length) return met[0];
    var smallest = plans.slice().sort(function (a, b) { return a.minQty - b.minQty; });
    return smallest[0] || null;
  }

  function resolveJavvyConfig(cart) {
    var stored = readStoredJavvyCfg();
    var plans = stored.plans.slice();
    var gifts = stored.gifts.slice();
    var tagged = false;
    var items = (cart && cart.items) || [];

    items.forEach(function (item) {
      var fromLine = lineJavvyCfg(item);
      if (!fromLine) return;
      if (fromLine.plans.length || fromLine.gifts.length) tagged = true;
      if (fromLine.plans.length) plans = fromLine.plans;
      if (fromLine.gifts.length) gifts = fromLine.gifts;
    });

    if (!plans.length && !gifts.length) return null;

    var planIds = {};
    plans.forEach(function (p) { planIds[String(p.sellingPlanId)] = true; });

    var hasJavvyLine = items.some(function (item) {
      if (parseInt(item.final_price, 10) <= 0) return false;
      if (item.properties && item.properties[JAVVY_CFG_PROP]) return true;
      var sp = lineSellingPlanId(item);
      return sp > 0 && !!planIds[String(sp)];
    });

    if (!tagged && !hasJavvyLine) return null;

    return { plans: plans, gifts: gifts, planIds: planIds };
  }

  function isJavvyPaidLine(item, javvy) {
    if (!item || parseInt(item.final_price, 10) <= 0) return false;
    if (item.properties && item.properties[JAVVY_CFG_PROP]) return true;
    var sp = lineSellingPlanId(item);
    return !!(javvy && sp > 0 && javvy.planIds[String(sp)]);
  }

  /**
   * Switch javvy flavor lines onto the selling plan that matches current box qty.
   * Returns the latest cart JSON (and sections when a change.js ran).
   */
  function remapJavvySellingPlans(cart, javvy) {
    if (!javvy || !javvy.plans.length) {
      return Promise.resolve({ changed: false, cart: cart, sections: null });
    }

    var flavorLines = (cart.items || []).filter(function (item) {
      return isJavvyPaidLine(item, javvy) && lineSellingPlanId(item) > 0;
    });
    if (!flavorLines.length) {
      return Promise.resolve({ changed: false, cart: cart, sections: null });
    }

    var paidQty = flavorLines.reduce(function (sum, item) {
      return sum + (Number(item.quantity) || 0);
    }, 0);
    var target = pickPlanForQty(javvy.plans, paidQty);
    if (!target) {
      return Promise.resolve({ changed: false, cart: cart, sections: null });
    }

    var toUpdate = flavorLines.filter(function (item) {
      return lineSellingPlanId(item) !== target.sellingPlanId;
    });
    if (!toUpdate.length) {
      return Promise.resolve({ changed: false, cart: cart, sections: null });
    }

    var latest = cart;
    var latestSections = null;
    var chain = Promise.resolve();
    toUpdate.forEach(function (item) {
      chain = chain.then(function () {
        return postChange({
          id: item.key,
          quantity: item.quantity,
          selling_plan: target.sellingPlanId
        }).then(function (res) {
          latest = res;
          latestSections = res.sections || latestSections;
        });
      });
    });

    return chain.then(function () {
      return { changed: true, cart: latest, sections: latestSections };
    });
  }

  function firstJavvyProductId(cart, javvy) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (isJavvyPaidLine(items[i], javvy)) return String(items[i].product_id);
    }
    return null;
  }

  function desiredQtyByVariant(tiers, paidQty) {
    var byVariant = {};
    (tiers || []).forEach(function (t) {
      var vid = String(Number(t.variantId));
      if (!byVariant[vid]) byVariant[vid] = [];
      byVariant[vid].push({ minQty: Number(t.minQty) || 0, quantity: Number(t.quantity) || 0 });
    });
    var desired = {};
    Object.keys(byVariant).forEach(function (vid) {
      var sorted = byVariant[vid].sort(function (a, b) { return b.minQty - a.minQty; });
      var active = null;
      for (var i = 0; i < sorted.length; i++) {
        if (paidQty >= sorted[i].minQty) {
          active = sorted[i];
          break;
        }
      }
      desired[vid] = active ? active.quantity : 0;
    });
    return desired;
  }

  function protectedClaimGiftIds() {
    try {
      var raw = localStorage.getItem('larineLaborCtaDiscount');
      if (!raw) raw = sessionStorage.getItem('larineLaborCtaDiscount');
      if (!raw) return {};
      var data = JSON.parse(raw);
      var map = {};
      function addList(list) {
        (list || []).forEach(function (id) {
          var n = parseInt(id, 10);
          if (n > 0) map[n] = true;
        });
      }
      if (data && data.claims && typeof data.claims === 'object' && !Array.isArray(data.claims)) {
        Object.keys(data.claims).forEach(function (id) {
          addList(data.claims[id] && data.claims[id].giftIds);
        });
      } else {
        addList(data && data.giftIds);
      }
      return map;
    } catch (e) {
      return {};
    }
  }

  function isProtectedGiftItem(item) {
    var props = item && item.properties;
    if (props) {
      if (String(props._labor_gift || props['_labor_gift'] || '') === '1') return true;
      if (String(props._enamel_gift || props['_enamel_gift'] || '') === '1') return true;
    }
    var vid = parseInt(item && item.variant_id, 10);
    if (!vid || !protectedClaimGiftIds()[vid]) return false;
    return parseInt(item.final_price, 10) === 0 || parseInt(item.price, 10) === 0;
  }

  function indexGiftLines(cart, treesVariantId) {
    var byOwner = {};
    var unowned = {};
    function addTo(map, vid, item) {
      if (!map[vid]) map[vid] = { qty: 0, keys: [] };
      map[vid].qty += item.quantity;
      if (item.key) map[vid].keys.push({ key: item.key, quantity: item.quantity });
    }
    ((cart && cart.items) || []).forEach(function (item) {
      if (parseInt(item.final_price, 10) !== 0) return;
      if (isProtectedGiftItem(item)) return;
      var vid = String(item.variant_id);
      if (treesVariantId && vid === treesVariantId) return;
      var owner = item.properties && item.properties[OWNER_PROP];
      if (owner) {
        owner = String(owner);
        if (!byOwner[owner]) byOwner[owner] = {};
        addTo(byOwner[owner], vid, item);
      } else {
        addTo(unowned, vid, item);
      }
    });
    return { byOwner: byOwner, unowned: unowned };
  }

  function reduceGiftLines(keys, removeQty, updates) {
    var left = removeQty;
    for (var i = 0; i < keys.length && left > 0; i++) {
      var line = keys[i];
      var qty = Number(line.quantity) || 0;
      if (qty <= 0 || !line.key) continue;
      if (qty <= left) {
        updates[line.key] = 0;
        left -= qty;
      } else {
        updates[line.key] = qty - left;
        left = 0;
      }
    }
  }

  function collectOwnerJobs(cart, cfg, javvy, themeTiers) {
    var jobs = [];
    var seen = {};
    function push(ownerId, jobTiers) {
      var list = (jobTiers || []).filter(function (t) {
        return t && t.variantId && Number(t.variantId) > 0;
      });
      if (!list.length) return;
      var key = ownerId ? String(ownerId) : '__none__';
      if (seen[key]) return;
      seen[key] = true;
      jobs.push({ ownerId: ownerId ? String(ownerId) : null, tiers: list });
    }
    if (javvy && javvy.gifts.length) {
      push(firstJavvyProductId(cart, javvy) || cfg.productId, javvy.gifts);
    }
    if (themeTiers && themeTiers.length) {
      push(cfg.productId || null, themeTiers);
    }
    return jobs;
  }

  /**
   * Works out the gift/tree line changes the cart needs (cart/update.js
   * `updates` keyed by line key or variant id, and cart/add.js `adds`)
   * without touching the cart.
   */
  function computeGiftChanges(cart, cfg, javvy) {
    var giftsEnabled = cfg.enabled !== false;
    var themeTiers = (cfg.tiers || []).filter(function (t) {
      return t && t.variantId && Number(t.variantId) > 0;
    });

    var treesVariantId = (cfg.trees && Number(cfg.trees.variantId) > 0)
      ? String(Number(cfg.trees.variantId))
      : null;

    var ownerJobs = giftsEnabled ? collectOwnerJobs(cart, cfg, javvy, themeTiers) : [];

    if (!ownerJobs.length && !treesVariantId) {
      return { updates: {}, adds: [], changed: false };
    }

    var cartQtyById = {};
    var paidByProduct = {};
    var totalPaid = 0;

    cart.items.forEach(function (item) {
      var vid = String(item.variant_id);
      cartQtyById[vid] = (cartQtyById[vid] || 0) + item.quantity;
      if (parseInt(item.final_price, 10) > 0 && vid !== treesVariantId) {
        var pid = String(item.product_id);
        paidByProduct[pid] = (paidByProduct[pid] || 0) + item.quantity;
        totalPaid += item.quantity;
      }
    });

    ownerJobs.forEach(function (job) {
      if (!job.ownerId) return;
      rememberOwners(job.tiers.map(function (t) { return String(Number(t.variantId)); }), job.ownerId);
    });

    var giftIndex = indexGiftLines(cart, treesVariantId);
    var unownedLeft = {};
    Object.keys(giftIndex.unowned).forEach(function (vid) {
      unownedLeft[vid] = giftIndex.unowned[vid].qty;
    });

    var updates = {};
    var adds = [];
    var changed = false;

    ownerJobs.forEach(function (job) {
      var paidQty = job.ownerId ? (paidByProduct[job.ownerId] || 0) : totalPaid;
      var desired = desiredQtyByVariant(job.tiers, paidQty);
      var tagged = job.ownerId
        ? (giftIndex.byOwner[job.ownerId] || {})
        : (giftIndex.unowned || {});

      if (paidQty <= 0) {
        Object.keys(tagged).forEach(function (vid) {
          if (!tagged[vid] || !tagged[vid].qty) return;
          reduceGiftLines(tagged[vid].keys, tagged[vid].qty, updates);
          changed = true;
        });
        return;
      }

      Object.keys(desired).forEach(function (vid) {
        var target = desired[vid];
        var taggedEntry = tagged[vid] || { qty: 0, keys: [] };
        var taggedQty = taggedEntry.qty;
        var current = taggedQty;
        if (job.ownerId && target > current) {
          var claimed = Math.min(unownedLeft[vid] || 0, target - current);
          if (claimed > 0) {
            unownedLeft[vid] = (unownedLeft[vid] || 0) - claimed;
            current += claimed;
          }
        }
        if (target === current) return;
        changed = true;
        if (target > current) {
          var add = { id: Number(vid), quantity: target - current };
          if (job.ownerId) {
            add.properties = {};
            add.properties[OWNER_PROP] = job.ownerId;
          }
          adds.push(add);
        } else if (taggedQty > target) {
          reduceGiftLines(taggedEntry.keys, taggedQty - target, updates);
        }
      });
    });

    if (treesVariantId) {
      var treesPaid = cfg.productId ? (paidByProduct[String(cfg.productId)] || 0) : totalPaid;
      var treesCurrent = cartQtyById[treesVariantId] || 0;
      if (treesPaid !== treesCurrent) {
        changed = true;
        if (treesCurrent === 0 && treesPaid > 0) {
          adds.push({ id: Number(treesVariantId), quantity: treesPaid });
        } else {
          updates[treesVariantId] = treesPaid;
        }
      }
    }

    var ownerMap = readOwnerMap();
    cart.items.forEach(function (item) {
      if (parseInt(item.final_price, 10) !== 0) return;
      if (treesVariantId && String(item.variant_id) === treesVariantId) return;
      if (item.key && updates[item.key] === 0) return;
      var owner = (item.properties && item.properties[OWNER_PROP])
        ? String(item.properties[OWNER_PROP])
        : '';
      if (!owner) {
        if (isProtectedGiftItem(item)) return;
        owner = giftOwner(item, ownerMap);
      }
      if (!owner) return;
      if (paidByProduct[owner] > 0) return;
      if (item.key && item.quantity > 0) {
        updates[item.key] = 0;
        changed = true;
      }
    });

    return { updates: updates, adds: adds, changed: changed };
  }

  /**
   * Gift/tree lines sync() would add for this cart. Lets a caller send them in
   * its own cart/add.js, so the sync after it has nothing to add and checkout
   * doesn't wait on a second add (~1s on 6+ box orders).
   */
  function plan(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    var cfg = window.__cartFreeGiftsConfig || {};
    return computeGiftChanges(cart, cfg, resolveJavvyConfig(cart)).adds;
  }

  // ── Main sync ──────────────────────────────────────────────────────────────

  /**
   * sync(cartData?)
   * If cartData is supplied (e.g. from a cart/change.js response that already
   * contains the cart items) we skip the extra GET to /cart.js and use it
   * directly.  This halves the number of API calls per mutation.
   */
  function sync(cartData) {
    if (window.__cartClearingOnBack) return Promise.resolve({ changed: false });
    var cfg = window.__cartFreeGiftsConfig || {};
    if (syncing) {
      syncPending = true;
      return new Promise(function (resolve) {
        syncWaiters.push(resolve);
      });
    }

    syncing = true;
    window.__cartFreeGiftsSyncing = true;

    var cartPromise = (cartData && Array.isArray(cartData.items))
      ? Promise.resolve(cartData)
      : fetchCart();

    return cartPromise.then(function (cart) {
      // ── Gift-only cart: nothing paid left, clear everything ──────────────
      if (isGiftOnlyCart(cart)) {
        return postClear().then(function () {
          var cleared = { changed: true };
          finishSync(cleared);
          return cleared;
        });
      }

      var savedDiscountCodes = discountCodesFromCart(cart);
      var javvy = resolveJavvyConfig(cart);
      return remapJavvySellingPlans(cart, javvy).then(function (remap) {
        cart = remap.cart || cart;
        javvy = resolveJavvyConfig(cart) || javvy;
        var planChanged = !!remap.changed;
        var planSections = remap.sections || null;

        var gifts = computeGiftChanges(cart, cfg, javvy);
        var updates = gifts.updates;
        var adds = gifts.adds;
        var changed = gifts.changed;

        if (!changed) {
          if (!planChanged) {
            var unchanged = { changed: false };
            finishSync(unchanged);
            return unchanged;
          }
          return restoreDiscountCodes(savedDiscountCodes, cart, planSections).then(function (restored) {
            var planned = { changed: true, sections: restored.sections || planSections };
            finishSync(planned);
            return planned;
          });
        }

        var latestSections = planSections;
        var giftChain = Promise.resolve();
        if (Object.keys(updates).length) {
          giftChain = giftChain.then(function () {
            return postUpdate(updates).then(function (res) { latestSections = res.sections; });
          });
        }
        if (adds.length) {
          giftChain = giftChain.then(function () {
            return postAdd(adds).then(function (res) { latestSections = res.sections; });
          });
        }

        return giftChain.then(function () {
          return restoreDiscountCodes(savedDiscountCodes, null, latestSections).then(function (restored) {
            var updated = { changed: true, sections: restored.sections || latestSections };
            finishSync(updated);
            return updated;
          });
        });
      });

    }).catch(function (e) {
      console.error('[cart-free-gifts] sync error:', e);
      var failed = { changed: false };
      finishSync(failed);
      return failed;
    });
  }

  // ── Public API ─────────────────────────────────────────────────────────────
  window.cartFreeGifts = { sync: sync, plan: plan, clearIfGiftOnly: clearIfGiftOnly };

  // ── Boot-time safety net ──────────────────────────────────────────────────
  // sync() only ever runs off a live cart mutation (add/change/update fetch).
  // A shopper landing on /cart (or anywhere else) with an already gift-only
  // cart — e.g. a reload right after the mutation that caused it — wouldn't
  // otherwise get cleaned up. Reuses cart-drawer.js's own refresh pipeline
  // (already loaded by this point — both are `defer` scripts, and deferred
  // scripts finish executing before DOMContentLoaded fires) so the drawer,
  // bubble, and — when present — the /cart page's own sections all update to
  // show the now-empty cart.
  document.addEventListener('DOMContentLoaded', function () {
    if (window.__cartClearingOnBack && window.__larineIsGumIgHideChrome && window.__larineIsGumIgHideChrome()) return;
    try {
      if (window.__larineIsGumIgHideChrome && window.__larineIsGumIgHideChrome()) {
        var skipGifts = sessionStorage.getItem('larineBundleCheckoutRedirect') === '1';
        if (!skipGifts) skipGifts = localStorage.getItem('larineBundleCheckoutRedirect') === '1';
        if (!skipGifts) {
          var parts = document.cookie.split(';');
          for (var i = 0; i < parts.length; i++) {
            if (parts[i].trim() === 'larineBundleCheckoutRedirect=1') { skipGifts = true; break; }
          }
        }
        if (skipGifts) return;
      }
    } catch (e) {}
    clearIfGiftOnly().then(function (cleared) {
      if (cleared && window.cartDrawerCustom && typeof window.cartDrawerCustom.refresh === 'function') {
        window.cartDrawerCustom.refresh();
      }
    });
  });

})();
